import { Show, SimpleShowLayout, useGetOne, Loading, usePermissions, useTranslate, Edit, SimpleForm, useDataProvider, useResourceContext } from 'react-admin';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import CustomCloseButton from '@/components/global/buttons/CustomCloseButton.jsx';
import DeviceLogs from '@/components/device/DeviceLogs.jsx';
import { CustomButton } from '@/components/global/buttons/CustomButton';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import DeviceErrorReportList from '@/components/device/DeviceErrorReportList.jsx';
import ServiceHistory from '@/components/service/ServiceHistory.jsx';
import { useEditingStates } from './hooks/useEditingStates';
import { useQuickEditField } from '@/composables/useQuickEditField.js';
import ServiceSummary, { SERVICE_SUMMARY_WORKING_TIME_ANCHOR_ID, SERVICE_SUMMARY_PLANNED_DATE_ANCHOR_ID } from './ServiceSummary';
import CollapsibleSection from '@/components/global/fields/CollapsibleSection';
import CustomMorePopover from '@/components/global/buttons/CustomMorePopover.jsx';
import CameraIcon from '@/assets/icons/camera-icon.svg?react';
import CommentsIcon from '@/assets/icons/comments-icon.svg?react';
import CalendarIcon from '@/assets/icons/calendar-icon.svg?react';
import ServiceAddIcon from '@/assets/icons/service-add-icon.svg?react';
import DeviceLogsHeader from '@/components/device/DeviceLogsHeader.jsx';
import { Palette } from '@/themes/themeOverrides/Palette';
import { ActivateDeviceActionButton, DeactivateDeviceActionButton, RestartDeviceActionButton } from '@/components/global/buttons/actionButtons.jsx';
import ActivityList from '@/components/service/activity/ActivityList.jsx';
import CommentInput from '@/components/service/activity/CommentInput.jsx';
import { scrollServiceDrawerToTop } from '@/helpers/ui/scrollServiceDrawerToTop.js';

const StyledServiceShow = styled(Box)(() => ({
    '& .device-issue-drawer': {
        '.RaSimpleShowLayout-root': {
            padding: '0 !important',
        },
    },
    '& .RaEdit-main.RaEdit-noActions': {
        margin: '0',
    },
    '& .RaShow-noActions': {
        margin: '0',
    },
    '& .popup-wrapper-top': {
        borderBottom: `2px solid ${Palette.gray.gray75}`,
        '& .popup-box--header': {
            padding: 0,
            '& small': {
                fontFamily: 'Inter, Arial, sans-serif',
                display: 'block',
                fontSize: '16px',
                fontWeight: 600,
                lineHeight: '22px',
                letterSpacing: '-0.16px',
                color: Palette.gray.gray500,
                marginTop: '2px',
            },
        },
    },
    '& .simple-show-layout': {
        padding: '0',
        position: 'relative',
        '& .popup-wrapper': {
            padding: '0 !important',
        },
    },
    '& .RaEdit-root': {
        width: '100%',
        '& .RaSimpleForm-root': {
            padding: '0 !important',
            width: '100%',
        },
    },
    '& .popup-box--header': {
        cursor: 'pointer',
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            padding: '20px 20px',
        },
        alignItems: 'center',
        '& h4': {
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                fontSize: '22px',
            },
        },
        '& .MuiIconButton-root': {
            padding: '12px !important',
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                padding: '8px !important',
            },
        },
    },
    '& .pos-field-name': {
        p: {
            margin: 0,
        },
    },
    '& .pos-field-name-id': {
        fontWeight: 600,
    },
    '& .pos-field-info': {
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            marginTop: '4px',
        },
        '& .pos-field-address': {
            fontSize: '14px',
            marginBottom: '4px',
        },
        '& .pos-field-phone': {
            fontSize: '14px',
        },
    },
    '& .ra-field.popup-wrapper': {
        padding: '0',
    },
    '& .ra-field': {
        '&.ra-field-serialNumber': {
            '& span.MuiTypography-body2': {
                display: 'flex',
                alignItems: 'center',
                fontSize: '14px !important',
                fontWeight: '400 !important',
            },
        },
    },
    '& .MuiStack-root': {
        margin: 0,
    },
    '& .details-simple-show-layout': {
        margin: '0',
        width: '100%',
        '& .select-full-width': {
            width: '100%',
        },
        '& .MuiFormControl-root.select-with-icon': {
            marginBottom: '4px',
            maxWidth: '310px',
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                maxWidth: '100%',
            },
        },
        '& .MuiStack-root.ra-field.select-with-icon': {
            borderTop: 'none',
            padding: '6px 0',
            '& .MuiFormHelperText-root': {
                display: 'none',
            },
            '& .MuiTypography-root.MuiTypography-body1.RaLabeled-label': {
                display: 'flex',
                alignItems: 'center',
                marginTop: '11px !important',
            },
        },
        '& .MuiStack-root.ra-field.select-with-icon--border': {
            borderTop: `2px solid ${Palette.gray.gray75}`,
            paddingTop: '32px',
        },
        '& .MuiStack-root.ra-field.select-with-icon-padding': {
            paddingBottom: '32px',
        },
        '& .MuiStack-root.ra-field': {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            borderTop: `2px solid ${Palette.gray.gray75}`,
            marginBottom: '0',
            padding: '16px 0',
            '&.field-container': {
                '& .field-container': {
                    width: '100%',
                },
            },
            width: '100%',
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                padding: '20px 0',
                flexDirection: 'column',
                alignItems: 'flex-start',
            },
            '&.ra-field-serialNumber': {
                '& span.MuiTypography-body2': {
                    fontSize: '18px',
                    fontFamily: 'Inter, Arial, sans-serif',
                    fontWeight: '700',
                },
            },
            '& span.MuiTypography-body2': {
                fontSize: '14px',
            },
        },
        '& .MuiStack-root.ra-field:last-of-type': {
            borderBottom: `2px solid ${Palette.gray.gray75}`,
            marginBottom: '0',
        },
    },
    '& .MuiTypography-root.RaLabeled-label': {
        fontSize: '14px !important',
        fontWeight: '400',
        lineHeight: '24px',
        width: '160px',
        minWidth: '160px',
        color: Palette.gray.gray700,
        margin: '0 !important',
        paddingRight: '16px',
    },
    '& .device-state-field-name': {
        fontWeight: 600,
    },
    '& .error-field-container': {
        fontWeight: 600,
        marginBottom: '4px',
        '& .MuiChip-label': {
            fontSize: '14px',
        },
        '& .error-icon': {
            display: 'none',
        },
        '& .info-icon': {
            [`@media (max-width: ${breakpoints.values.lg}px)`]: {
                display: 'none',
            },
            display: 'flex',
            alignItems: 'center',
        },
    },
    '& .error-title-container': {
        marginBottom: '4px',
    },
    '& .MuiButtonBase-root.close-popup': {
        padding: '12px 16px !important',
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            padding: '12px !important',
        },
    },
    '& .MuiButtonBase-root': {
        '& .MuiCircularProgress-root': {
            display: 'none',
        },
    },
    '& .MuiTypography-h5': {
        fontSize: '22px',
    },
    '& .present-state': {
        display: 'flex',
        alignItems: 'center',
    },
    '& .MuiCard-root.scroll-container': {
        paddingBottom: '4px',
    },
    '& .MuiToolbar-root.MuiToolbar-gutters.MuiTablePagination-toolbar': {
        borderBottom: `2px solid ${Palette.gray.gray75}`,
        padding: '9px 0',
    },
    '& .device-error-field': {
        '& .error-time': {
            display: 'block !important',
        },
    },
    '& .chip-small': {
        height: '20px',
        borderRadius: '6px',
        '& .MuiChip-label': {
            padding: '0 6px',
        },
    },
    '& .device-error-report-list-wrapper': {
        '& .MuiChip-root': {
            borderRadius: '8px',
            '& .MuiChip-label': {
                padding: '0 6px',
                fontSize: '14px',
            },
        },
        '& .chips-container': {
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                flexWrap: 'wrap',
                margin: '8px 14px 8px 14px',
                pointerEvents: 'none',
                gap: '3px',
            },
            '& .MuiChip-root': {
                [`@media (max-width: ${breakpoints.values.md}px)`]: {
                    margin: '0 !important',
                },
            },
        },
        '& .custom-select-array-input .MuiFormControl-root': {
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                height: 'auto !important',
            },
        },
    },
}));

const MoreOptionsPopover = ({ record, buttonLabel, onRefresh, onOpenWorkingTime, onOpenComment, onOpenPlannedDate }) => {
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const resource = useResourceContext();
    const isServiceInspection = resource === 'service-inspection';

    return (
        <CustomMorePopover record={record} buttonLabel={buttonLabel}>
            {(permissions.isAdmin || permissions.isServiceMaster) && (
                <RestartDeviceActionButton
                    id={record.device.id}
                    label={translate('resources.device.labels.restartDevice')}
                    buttonProps={{ 'data-keep-popover-open': true }}
                />
            )}
            {(permissions.isAdmin || permissions.isServiceMaster) &&
                (record.device.isActive ? (
                    <DeactivateDeviceActionButton
                        id={record.device.id}
                        label={translate('resources.device.labels.deactivateDevice')}
                        onActionSuccess={onRefresh}
                        buttonProps={{ 'data-keep-popover-open': true }}
                    />
                ) : (
                    <ActivateDeviceActionButton
                        id={record.device.id}
                        label={translate('resources.device.labels.activateDevice')}
                        onActionSuccess={onRefresh}
                        buttonProps={{ 'data-keep-popover-open': true }}
                    />
                ))}
            <CustomButton
                variant='text'
                sx={{ width: '100%', justifyContent: 'flex-start' }}
                label={translate('resources.serviceRequest.labels.addWorkTime')}
                startIcon={<ServiceAddIcon />}
                onClick={onOpenWorkingTime}
            />
            <CustomButton
                variant='text'
                sx={{ width: '100%', justifyContent: 'flex-start' }}
                label={translate('resources.serviceRequest.labels.addComment')}
                startIcon={<CommentsIcon />}
                onClick={onOpenComment}
            />
            <CustomButton
                variant='text'
                sx={{ width: '100%', justifyContent: 'flex-start' }}
                label={translate('resources.serviceRequest.labels.addPhoto')}
                startIcon={<CameraIcon />}
                onClick={onOpenComment}
            />
            {isServiceInspection && (
                <CustomButton
                    variant='text'
                    sx={{ width: '100%', justifyContent: 'flex-start' }}
                    label={translate('resources.serviceRequest.labels.addVisit')}
                    startIcon={<CalendarIcon />}
                    onClick={onOpenPlannedDate}
                />
            )}
        </CustomMorePopover>
    );
};

export const ServiceShow = ({ handleClose, elementId, resource, type, drawerSection }) => {
    const editingStates = useEditingStates();
    const resourceContext = useResourceContext();
    const resolvedResource = resource || resourceContext;
    const { save, isLoadingEdit } = useQuickEditField(resolvedResource, elementId, editingStates);
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const { data: initialData, isLoading } = useGetOne(resolvedResource, { id: elementId }, { enabled: !!resolvedResource && !!elementId });
    const [serviceRequestData, setServiceRequestData] = useState(null);
    const [isErrorsOpen, setIsErrorsOpen] = useState(false);
    const [isServiceHistoryOpen, setIsServiceHistoryOpen] = useState(false);
    const [isLogsOpen, setIsLogsOpen] = useState(false);
    const [logsRequested, setLogsRequested] = useState(false);
    const { permissions } = usePermissions();
    const [isSummaryOpen, setIsSummaryOpen] = useState(true);
    const shouldOpenActivities = drawerSection === 'activities' || type?.endsWith('-activities');
    const [isActivitiesOpen, setIsActivitiesOpen] = useState(!!shouldOpenActivities);
    const [refreshKey, setRefreshKey] = useState(0);
    const activitiesRef = useRef(null);
    const headerRef = useRef(null);
    const [pendingScrollTarget, setPendingScrollTarget] = useState(null);
    const shouldOpenActivitiesRef = useRef(shouldOpenActivities);
    const pendingScrollTargetRef = useRef(pendingScrollTarget);
    const [autoOpenComment, setAutoOpenComment] = useState(false);

    shouldOpenActivitiesRef.current = shouldOpenActivities;
    pendingScrollTargetRef.current = pendingScrollTarget;

    const resolvedData = serviceRequestData || initialData;

    useEffect(() => {
        if (initialData) {
            setServiceRequestData(initialData);
        }
    }, [initialData]);

    useEffect(() => {
        if (refreshKey > 0 && elementId) {
            dataProvider
                .getOne(resolvedResource, { id: elementId })
                .then(({ data }) => {
                    setServiceRequestData(data);
                })
                .catch(err => {
                    console.error('Error refreshing service request data:', err);
                });
        }
    }, [refreshKey, elementId, dataProvider, resolvedResource]);

    useEffect(() => {
        if (shouldOpenActivities && resolvedData) {
            setIsActivitiesOpen(true);
        }
    }, [shouldOpenActivities, resolvedData]);

    useEffect(() => {
        if (!elementId || isLoading) return;
        if (shouldOpenActivitiesRef.current || pendingScrollTargetRef.current) return;

        const timeout = setTimeout(() => scrollServiceDrawerToTop(headerRef.current), 50);

        return () => clearTimeout(timeout);
    }, [elementId, isLoading]);

    useEffect(() => {
        if (!shouldOpenActivities || !isActivitiesOpen || !resolvedData) return;
        const timeout = setTimeout(() => {
            activitiesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
        return () => clearTimeout(timeout);
    }, [shouldOpenActivities, isActivitiesOpen, resolvedData]);

    useEffect(() => {
        if (!pendingScrollTarget || !resolvedData) return;
        const isActivitiesTarget = pendingScrollTarget === 'service-show-activities';
        const canScroll = isActivitiesTarget ? isActivitiesOpen : isSummaryOpen;
        if (!canScroll) return;
        const timeout = setTimeout(() => {
            const target = document.getElementById(pendingScrollTarget);
            if (target) {
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                if (pendingScrollTarget === SERVICE_SUMMARY_WORKING_TIME_ANCHOR_ID) {
                    const input = target.querySelector('input[name="workingTime"]');
                    input?.focus();
                }
                if (pendingScrollTarget === SERVICE_SUMMARY_PLANNED_DATE_ANCHOR_ID) {
                    const input = target.querySelector('input[name="scheduledDate"]');
                    input?.focus();
                }
            }
            setPendingScrollTarget(null);
        }, 50);
        return () => clearTimeout(timeout);
    }, [pendingScrollTarget, resolvedData, isSummaryOpen, isActivitiesOpen]);

    const handleRefresh = () => {
        setRefreshKey(prev => prev + 1);
    };

    const handleOpenWorkingTime = () => {
        setIsActivitiesOpen(false);
        setIsErrorsOpen(false);
        setIsLogsOpen(false);
        setIsServiceHistoryOpen(false);
        setIsSummaryOpen(true);
        editingStates.startEditingWorkingTime();
        setPendingScrollTarget(SERVICE_SUMMARY_WORKING_TIME_ANCHOR_ID);
    };

    const handleOpenPlannedDate = () => {
        setIsActivitiesOpen(false);
        setIsErrorsOpen(false);
        setIsLogsOpen(false);
        setIsServiceHistoryOpen(false);
        setIsSummaryOpen(true);
        editingStates.startEditingPlannedDate();
        setPendingScrollTarget(SERVICE_SUMMARY_PLANNED_DATE_ANCHOR_ID);
    };

    const handleOpenComment = () => {
        setIsSummaryOpen(false);
        setIsErrorsOpen(false);
        setIsLogsOpen(false);
        setIsServiceHistoryOpen(false);
        setIsActivitiesOpen(true);
        setPendingScrollTarget('service-show-activities');
        setAutoOpenComment(true);
    };

    if (isLoading || !resolvedData) return <Loading />;

    const isAdminOrServiceMaster = permissions.isAdmin || permissions.isServiceMaster;
    const isBok = permissions.isBok;
    const isServiceRegular = permissions.isService && !permissions.isServiceMaster;
    const isClient = permissions.isClient || permissions.isClientMaster;
    const defaultStatus = permissions.isServiceMaster ? 'accepted' : 'new';
    const canSeeHiddenComments = isAdminOrServiceMaster || isBok;

    if (isLoading || isLoadingEdit) {
        return <Loading />;
    }

    const formattedId = resolvedData.customId.toString().padStart(6, '0');

    const logsHeaderContent = <DeviceLogsHeader deviceId={resolvedData?.device?.id} logsRequested={logsRequested} />;

    return (
        <StyledServiceShow>
            <Box ref={headerRef} className='popup-wrapper popup-wrapper-top'>
                <Box className='popup-box popup-box--header'>
                    <Typography variant='h4'>
                        {resolvedResource === 'service-inspection' ? (
                            <>
                                {translate('resources.serviceInspection.labels.requestTitle')}
                                <small>#{formattedId}</small>
                            </>
                        ) : (
                            translate('resources.serviceRequest.labels.editServiceRequestTitle', { id: formattedId })
                        )}
                    </Typography>
                    <MoreOptionsPopover
                        record={resolvedData}
                        buttonLabel={translate('common.options')}
                        onRefresh={handleRefresh}
                        onOpenWorkingTime={handleOpenWorkingTime}
                        onOpenComment={handleOpenComment}
                        onOpenPlannedDate={handleOpenPlannedDate}
                    />
                    <Box>
                        <CustomCloseButton className='close-button' onClose={handleClose} />
                    </Box>
                </Box>
            </Box>
            <Show id={elementId} actions={false} resource={resolvedResource}>
                <SimpleShowLayout className='simple-show-layout'>
                    <CollapsibleSection title={translate('campaign.steps.summary')} isOpen={isSummaryOpen} onToggle={() => setIsSummaryOpen(!isSummaryOpen)}>
                        <ServiceSummary
                            elementId={elementId}
                            save={save}
                            editingStates={editingStates}
                            defaultStatus={defaultStatus}
                            canEditAllFields={isAdminOrServiceMaster}
                            canEditLimitedFields={isServiceRegular}
                            resource={resolvedResource}
                        />
                    </CollapsibleSection>

                    {(isAdminOrServiceMaster || isBok || isServiceRegular) && (
                        <Box ref={activitiesRef} id='service-show-activities'>
                            <CollapsibleSection
                                title={translate('resources.device.labels.activities')}
                                isOpen={isActivitiesOpen}
                                onToggle={() => setIsActivitiesOpen(!isActivitiesOpen)}
                                commentCount={canSeeHiddenComments ? resolvedData.changeCount : resolvedData.visibleChangeCount}
                            >
                                <Edit
                                    id={elementId}
                                    actions={false}
                                    resource={resolvedResource}
                                    sx={{
                                        '& .MuiCardContent-root': {
                                            marginBottom: '0 !important',
                                        },
                                    }}
                                >
                                    <SimpleForm toolbar={false}>
                                        <CommentInput
                                            source='activities'
                                            serviceRequestId={resolvedResource === 'service-request' ? elementId : null}
                                            serviceInspectionId={resolvedResource === 'service-inspection' ? elementId : null}
                                            resource={resolvedResource}
                                            autoOpen={autoOpenComment}
                                            onAutoOpenHandled={() => setAutoOpenComment(false)}
                                        />
                                    </SimpleForm>
                                </Edit>
                                {isActivitiesOpen && (
                                    <Box className='device-error-report-list-wrapper' sx={{ marginTop: '10px' }}>
                                        <ActivityList elementId={elementId} record={resolvedData} resource={resolvedResource} />
                                    </Box>
                                )}
                            </CollapsibleSection>
                        </Box>
                    )}

                    <CollapsibleSection
                        title={translate('resources.device.labels.lastErrors')}
                        isOpen={isErrorsOpen}
                        onToggle={() => setIsErrorsOpen(!isErrorsOpen)}
                    >
                        <Box className='device-error-report-list-wrapper'>
                            <DeviceErrorReportList deviceId={resolvedData.device.id} shouldExtend={true} />
                        </Box>
                    </CollapsibleSection>

                    {!isClient && (
                        <CollapsibleSection
                            title={translate('resources.device.labels.logs')}
                            isOpen={isLogsOpen}
                            onToggle={() => setIsLogsOpen(!isLogsOpen)}
                            headerContent={logsHeaderContent}
                        >
                            <DeviceLogs className='device-logs' deviceId={resolvedData.device.id} onLogsRequestedChange={setLogsRequested} />
                        </CollapsibleSection>
                    )}

                    <CollapsibleSection
                        title={translate('resources.device.labels.serviceHistory')}
                        isOpen={isServiceHistoryOpen}
                        onToggle={() => setIsServiceHistoryOpen(!isServiceHistoryOpen)}
                    >
                        <ServiceHistory deviceId={resolvedData.device.id} shouldExtend={true} />
                    </CollapsibleSection>
                </SimpleShowLayout>
            </Show>
        </StyledServiceShow>
    );
};

MoreOptionsPopover.propTypes = {
    record: PropTypes.shape({
        device: PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            isActive: PropTypes.bool,
        }).isRequired,
    }).isRequired,
    buttonLabel: PropTypes.string.isRequired,
    onRefresh: PropTypes.func.isRequired,
    onOpenWorkingTime: PropTypes.func,
    onOpenComment: PropTypes.func,
    onOpenPlannedDate: PropTypes.func,
};

ServiceShow.propTypes = {
    elementId: PropTypes.string.isRequired,
    handleClose: PropTypes.func.isRequired,
    resource: PropTypes.string,
    type: PropTypes.string,
    drawerSection: PropTypes.string,
};
